<template>
  <div>
    <h3>Invoice Management Component Usage Example</h3>
    
    <!-- DEMONSTRATION: Default Radio Button Behavior -->
    <div class="demo-section">
      <h3>🎯 Default Radio Button Behavior Demonstration</h3>
      <p><strong>Rule:</strong> New invoices (no payment ID) default to "Normal", Existing invoices (with payment ID) default to "Supplementary"</p>
    </div>

    <!-- NEW INVOICES - Default to "Normal" -->
    <div class="example-section new-invoice">
      <h4>🆕 NEW CC Export Invoice (paymentId = "") → Defaults to "Normal"</h4>
      <InvoiceManagementComponent
        :config="ccExportConfig"
        :moduleDetail="ccExportDetail"
        :paymentDetail="ccExportNewPaymentDetail"
        :invoiceList="ccExportInvoiceList"
        :showRevisedMailCheckbox="true"
        :assignmentNumber="ccExportDetail.ccImportSeries"
        @add-invoice="addCCExportInvoice"
        @remove-invoice="removeCCExportInvoice"
        @remove-invoice-currency="removeCCExportInvoiceCurrency"
        @save-and-submit="saveCCExportInvoiceData" />
    </div>

    <div class="example-section new-invoice">
      <h4>🆕 NEW FF Export Invoice (paymentId = "") → Defaults to "Normal"</h4>
      <InvoiceManagementComponent
        :config="ffExportConfig"
        :moduleDetail="ffExportDetail"
        :paymentDetail="ffExportNewPaymentDetail"
        :invoiceList="ffExportInvoiceList"
        :showRevisedMailCheckbox="true"
        :assignmentNumber="ffExportDetail.ffExportFclSeries"
        @add-invoice="addFFExportInvoice"
        @remove-invoice="removeFFExportInvoice"
        @remove-invoice-currency="removeFFExportInvoiceCurrency"
        @save-and-submit="saveFFExportInvoiceData" />
    </div>

    <!-- EXISTING INVOICES - Default to "Supplementary" -->
    <div class="example-section existing-invoice">
      <h4>📋 EXISTING CC Export Invoice (paymentId = "CC_PAY_001") → Defaults to "Supplementary"</h4>
      <InvoiceManagementComponent
        :config="ccExportConfig"
        :moduleDetail="ccExportDetail"
        :paymentDetail="ccExportExistingPaymentDetail"
        :invoiceList="ccExportInvoiceList"
        :showRevisedMailCheckbox="true"
        :assignmentNumber="ccExportDetail.ccImportSeries"
        @add-invoice="addCCExportInvoice"
        @remove-invoice="removeCCExportInvoice"
        @remove-invoice-currency="removeCCExportInvoiceCurrency"
        @save-and-submit="saveCCExportInvoiceData" />
    </div>

    <div class="example-section existing-invoice">
      <h4>📋 EXISTING FF Export Invoice (paymentId = "FF_PAY_001") → Defaults to "Supplementary"</h4>
      <InvoiceManagementComponent
        :config="ffExportConfig"
        :moduleDetail="ffExportDetail"
        :paymentDetail="ffExportExistingPaymentDetail"
        :invoiceList="ffExportInvoiceList"
        :showRevisedMailCheckbox="true"
        :assignmentNumber="ffExportDetail.ffExportFclSeries"
        @add-invoice="addFFExportInvoice"
        @remove-invoice="removeFFExportInvoice"
        @remove-invoice-currency="removeFFExportInvoiceCurrency"
        @save-and-submit="saveFFExportInvoiceData" />
    </div>

    <!-- ADDITIONAL EXAMPLES: Different Module Types -->
    <div class="demo-section">
      <h3>📋 Additional Module Type Examples</h3>
    </div>

    <!-- CC Import Example -->
    <div class="example-section">
      <h4>CC Import - Existing Invoice (paymentId = "CC_IMP_PAY_001") → Defaults to "Supplementary"</h4>
      <InvoiceManagementComponent
        :config="ccImportConfig"
        :moduleDetail="ccImportDetail"
        :paymentDetail="ccImportExistingPaymentDetail"
        :invoiceList="ccImportInvoiceList"
        :showRevisedMailCheckbox="true"
        :assignmentNumber="ccImportDetail.ccImportSeries"
        @add-invoice="addCCImportInvoice"
        @remove-invoice="removeCCImportInvoice"
        @remove-invoice-currency="removeCCImportInvoiceCurrency"
        @save-and-submit="saveCCImportInvoiceData" />
    </div>

    <!-- FF Import Example -->
    <div class="example-section">
      <h4>FF Import - Existing Invoice (paymentId = "FF_IMP_PAY_001") → Defaults to "Supplementary"</h4>
      <InvoiceManagementComponent
        :config="ffImportConfig"
        :moduleDetail="ffImportDetail"
        :paymentDetail="ffImportExistingPaymentDetail"
        :invoiceList="ffImportInvoiceList"
        :showRevisedMailCheckbox="true"
        :assignmentNumber="ffImportDetail.ffImportFclSeries"
        @add-invoice="addFFImportInvoice"
        @remove-invoice="removeFFImportInvoice"
        @remove-invoice-currency="removeFFImportInvoiceCurrency"
        @save-and-submit="saveFFImportInvoiceData" />
    </div>

    <!-- MISC Invoice Example -->
    <div class="example-section">
      <h4>MISC Invoice - Existing Invoice (paymentId = "MISC_PAY_001") → Defaults to "Supplementary"</h4>
      <InvoiceManagementComponent
        :config="miscInvoiceConfig"
        :moduleDetail="miscInvoiceDetail"
        :paymentDetail="miscInvoiceExistingPaymentDetail"
        :invoiceList="miscInvoiceList"
        :showRevisedMailCheckbox="true"
        :assignmentNumber="miscInvoiceDetail.miscSeries"
        @add-invoice="addMiscInvoice"
        @remove-invoice="removeMiscInvoice"
        @remove-invoice-currency="removeMiscInvoiceCurrency"
        @save-and-submit="saveMiscInvoiceData" />
    </div>
  </div>
</template>

<script>
import InvoiceManagementComponent from './InvoiceManagementComponent.vue';
import { getConfigByFileName, getConfigByModuleType } from "../../helper/InvoiceConfigHelper";
import { globalPermissionStatus } from "../../helper/collection";

export default {
  name: "InvoiceManagementExample",
  components: {
    InvoiceManagementComponent
  },
  data() {
    return {
      // Specific configurations for different component types
      // Normal Invoice Configurations
      ccExportConfig: getConfigByModuleType('CC_EXPORT', 'Normal'),
      ccImportConfig: getConfigByModuleType('CC_IMPORT', 'Normal'),
      ffExportConfig: getConfigByModuleType('FF_EXPORT', 'Normal'),
      ffImportConfig: getConfigByModuleType('FF_IMPORT', 'Normal'),
      miscInvoiceConfig: getConfigByModuleType('MISC_INVOICE', 'Normal'),
      
      // Supplementary Invoice Configurations
      ccExportSupplementaryConfig: getConfigByModuleType('CC_EXPORT', 'Supplementary'),
      ccImportSupplementaryConfig: getConfigByModuleType('CC_IMPORT', 'Supplementary'),
      ffExportSupplementaryConfig: getConfigByModuleType('FF_EXPORT', 'Supplementary'),
      ffImportSupplementaryConfig: getConfigByModuleType('FF_IMPORT', 'Supplementary'),
      miscInvoiceSupplementaryConfig: getConfigByModuleType('MISC_INVOICE', 'Supplementary'),
      ffSupplementaryConfig: getConfigByFileName('ffConSLineSuplimentryPayment_VP.vue'),

      // Module Detail Data - Based on actual component patterns
      ccExportDetail: {
        allModuleId: "CC123456",
        ccImportSeries: "CC/2024/001",
        clientName: "ABC Company",
        shippingLine: "Maersk Line"
      },
      ccImportDetail: {
        allModuleId: "CC_IMP123456",
        ccImportSeries: "CC/IMP/2024/001",
        clientName: "DEF Company",
        shippingLine: "Hapag-Lloyd"
      },
      ffExportDetail: {
        allModuleId: "FF123456",
        ffExportFclSeries: "FF/2024/001",
        clientName: "XYZ Company",
        shippingLine: "CMA CGM"
      },
      ffImportDetail: {
        allModuleId: "FF_IMP123456",
        ffImportFclSeries: "FF/IMP/2024/001",
        clientName: "GHI Company",
        shippingLine: "COSCO"
      },
      miscInvoiceDetail: {
        allModuleId: "MISC123456",
        miscSeries: "MISC/2024/001",
        clientName: "JKL Company",
        shippingLine: "OOCL"
      },
      ffSupplementaryDetail: {
        allModuleId: "FF_SUPP123456",
        ffExportFclConsoSeries: "FF/SUPP/2024/001",
        clientName: "ABC Shipping",
        shippingLine: "MSC"
      },

      // Payment Detail Data - Demonstrating default radio button behavior
      // NEW INVOICES (paymentId = "") -> Default to "Normal"
      ccExportNewPaymentDetail: {
        paymentId: "",  // Empty = New invoice -> defaults to "Normal"
        allModuleId: "CC123456",
        paymentDone: "0"
      },
      ffExportNewPaymentDetail: {
        paymentId: "",  // Empty = New invoice -> defaults to "Normal"
        allModuleId: "FF123456",
        paymentDone: "0"
      },
      
      // EXISTING INVOICES (paymentId exists) -> Default to "Supplementary"
      ccExportExistingPaymentDetail: {
        paymentId: "CC_PAY_001",  // Has payment ID -> defaults to "Supplementary"
        allModuleId: "CC123456",
        paymentDone: "0"
      },
      ffExportExistingPaymentDetail: {
        paymentId: "FF_PAY_001",  // Has payment ID -> defaults to "Supplementary"
        allModuleId: "FF123456",
        paymentDone: "0"
      },
      ccImportExistingPaymentDetail: {
        paymentId: "CC_IMP_PAY_001",
        allModuleId: "CC_IMP123456",
        paymentDone: "0"
      },
      ffImportExistingPaymentDetail: {
        paymentId: "FF_IMP_PAY_001",
        allModuleId: "FF_IMP123456",
        paymentDone: "0"
      },
      miscInvoiceExistingPaymentDetail: {
        paymentId: "MISC_PAY_001",
        allModuleId: "MISC123456",
        paymentDone: "0"
      },
      ffSupplementaryPaymentDetail: {
        paymentId: "FF_SUPP_PAY_001",
        allModuleId: "FF_SUPP123456",
        paymentDone: "0"
      },

      // Invoice Lists
      ccExportInvoiceList: [],
      ccImportInvoiceList: [],
      ffExportInvoiceList: [],
      ffImportInvoiceList: [],
      miscInvoiceList: [],
      ffSupplementaryInvoiceList: [],
    };
  },
  methods: {
    // CC Export Methods
    addCCExportInvoice(invoice) {
      this.ccExportInvoiceList.push(invoice);
    },
    removeCCExportInvoice(index) {
      this.ccExportInvoiceList.splice(index, 1);
    },
    removeCCExportInvoiceCurrency(invIndex, currIndex) {
      this.ccExportInvoiceList[invIndex].invCurrencyList.splice(currIndex, 1);
    },
    saveCCExportInvoiceData() {
      alert('CC Export Invoice Data Saved! (Validation passed)');
      console.log('CC Export Invoice Data:', this.ccExportInvoiceList);
    },

    // CC Import Methods
    addCCImportInvoice(invoice) {
      this.ccImportInvoiceList.push(invoice);
    },
    removeCCImportInvoice(index) {
      this.ccImportInvoiceList.splice(index, 1);
    },
    removeCCImportInvoiceCurrency(invIndex, currIndex) {
      this.ccImportInvoiceList[invIndex].invCurrencyList.splice(currIndex, 1);
    },
    saveCCImportInvoiceData() {
      alert('CC Import Invoice Data Saved! (Validation passed)');
      console.log('CC Import Invoice Data:', this.ccImportInvoiceList);
    },

    // FF Export Methods
    addFFExportInvoice(invoice) {
      this.ffExportInvoiceList.push(invoice);
    },
    removeFFExportInvoice(index) {
      this.ffExportInvoiceList.splice(index, 1);
    },
    removeFFExportInvoiceCurrency(invIndex, currIndex) {
      this.ffExportInvoiceList[invIndex].invCurrencyList.splice(currIndex, 1);
    },
    saveFFExportInvoiceData() {
      alert('FF Export Invoice Data Saved! (Validation passed)');
      console.log('FF Export Invoice Data:', this.ffExportInvoiceList);
    },

    // FF Import Methods
    addFFImportInvoice(invoice) {
      this.ffImportInvoiceList.push(invoice);
    },
    removeFFImportInvoice(index) {
      this.ffImportInvoiceList.splice(index, 1);
    },
    removeFFImportInvoiceCurrency(invIndex, currIndex) {
      this.ffImportInvoiceList[invIndex].invCurrencyList.splice(currIndex, 1);
    },
    saveFFImportInvoiceData() {
      alert('FF Import Invoice Data Saved! (Validation passed)');
      console.log('FF Import Invoice Data:', this.ffImportInvoiceList);
    },

    // MISC Invoice Methods
    addMiscInvoice(invoice) {
      this.miscInvoiceList.push(invoice);
    },
    removeMiscInvoice(index) {
      this.miscInvoiceList.splice(index, 1);
    },
    removeMiscInvoiceCurrency(invIndex, currIndex) {
      this.miscInvoiceList[invIndex].invCurrencyList.splice(currIndex, 1);
    },
    saveMiscInvoiceData() {
      alert('MISC Invoice Data Saved! (Validation passed)');
      console.log('MISC Invoice Data:', this.miscInvoiceList);
    },
  }
};
</script>

<style scoped>
.demo-section {
  margin: 2rem 0;
  padding: 1rem;
  background-color: #f8f9fa;
  border-left: 4px solid #007bff;
  border-radius: 0.25rem;
}

.demo-section h3 {
  color: #007bff;
  margin-bottom: 0.5rem;
}

.example-section {
  margin-bottom: 2rem;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
}

.example-section h4 {
  color: #007bff;
  margin-bottom: 1rem;
}

/* Visual distinction for new vs existing invoices */
.new-invoice {
  border-left: 4px solid #28a745; /* Green for new invoices */
  background-color: #f8fff9;
}

.new-invoice h4 {
  color: #28a745;
}

.existing-invoice {
  border-left: 4px solid #ffc107; /* Yellow for existing invoices */
  background-color: #fffdf5;
}

.existing-invoice h4 {
  color: #e0a800;
}
</style>



// WEBPACK FOOTER //
// InvoiceManagementExample.vue?b6b24c72